import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import styles from "../../style.module.scss";
import baseStyles from "../../base.module.scss";
import copy from "../../../../../../assets/svg/icons/common/copy";
import file from "../../../../../../assets/svg/icons/common/file";
import FileIcon from "../../../../../../assets/svg/icons/common/FileIcon";
import download from "../../../../../../assets/svg/icons/common/Download";
import ButtonIcon from "../../../../../../components/ButtonIcon/ButtonIcon";
import NotesSection from "./NotesSection";
import ApplicationStepper from "./ApplicationStepper";
import useSalesFormStore from "../../../../../../stores/sales-form";
import Axios from "axios";
import config from "../../../../../../../config";
import Spinner from "../../../../../../components/Loaders/Spinner";
import { getPdfUrl } from "../../../../../../../components/helper/helper";
import {
  CheckIcon,
  LockIcon,
  CheckCircleIcon,
  CircleIcon
} from "./icons";
import { FileIconColorMapping } from "../file-upload/utils";
import FileTypeModal from "../file-upload/FileTypeModal";
import { uploadApplicationFiles } from "../../../../../../../api";

const stratusURL = config.paymentcloud.STRATUS_URL;

/**
 * ThankYou component
 * Displayed when an application is signed (progression_id = 8)
 * Shows application status, required documents, and notes section
 */
const ThankYou = ({ appData, appSlug, reRender }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [signedFiles, setSignedFiles] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [agreements, setAgreements] = useState([
    {
      name: 'Merchant Agreement 2',
      type: 'pdf',
      size: '100KB'
    },
    {
      name: 'Merchant Agreement',
      type: 'pdf',
      size: '100KB'
    }
  ]);
  const [documentRequirements, setDocumentRequirements] = useState([
    { name: "Photo of driver's license", required: true, type: "Drivers License" },
    { name: "Voided check", required: true, type: "Voided Check" },
    { name: "3 months of bank statements", required: true, type: "Bank Statements" },
    { name: "3 months of processing statements", required: false, type: "Processing Statements" }
  ]);

  const [currentStep, setCurrentStep] = useState(8);
  const [isLoading, setIsLoading] = useState(true);
  const [showTypeModal, setShowTypeModal] = useState(false);

  // from store
  const applicationData = useSalesFormStore((state) => state.applicationData);
  const signerType = useSalesFormStore((state) => state.signerType);
  const appUploadedFiles = useSalesFormStore((state) => state.uploadedFiles);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (appData && appData.files) {
      setUploadedFiles(appData.files);
    }
    if (appData && appData.agreements) {
      setAgreements(appData.agreements);
    }

    if (appData && appData.progression_id) {
      // Map progression_id to step
      const progressionMap = {
        8: 1, // App submitted/signed
        9: 2, // Verification
        10: 3, // Approval
        11: 4, // Deployment
        12: 5  // Done
      };

      setCurrentStep(progressionMap[appData.progression_id] || 1);
    }
  }, [appData, appSlug]);

  const handleFileSelect = (e) => {
    try {
      const files = e.target.files;
      let filesArray = [];
      Object.entries(files).forEach(([key, value]) => {
        filesArray.push(value);
      });

      setFilesToUpload([...filesToUpload, ...filesArray]);
      setShowTypeModal(true);
    } catch (error) {
      console.error('Error selecting files:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    let filesArray = [];
    Object.entries(files).forEach(([key, value]) => {
      filesArray.push(value);
    });

    setFilesToUpload([...filesToUpload, ...filesArray]);
    setShowTypeModal(true);
  };


  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => toast.success("Link copied to clipboard"))
      .catch(() => toast.error("Failed to copy link"));
  };

  const renderFileIcon = (fileName = "") => {
    if (!fileName) {
      return <FileIcon color="#E03B24" />
    }
    const extension = fileName.split('.').pop();
    return (
      <FileIcon color={FileIconColorMapping[extension]} />
    )
  };

  const Copy = () => React.createElement(copy);
  const DownloadSimple = () => React.createElement(download);

  const isSignedPdfExist = async (url) => {
    return await Axios.get(url)
      .then((res) => {
        if (res?.status === 200) {
          return true;
        } else {
          return false;
        }
      })
      .catch(() => {
        return false;
      });
  };

  useEffect(() => {
    let isMounted = true;

    const checkFiles = async () => {
      try {
        if (!applicationData?.override_bank?.length) {
          setIsLoading(false);
          return;
        }

        const signedFiles = [];
        const checkPromises = applicationData.override_bank.map(async (item) => {
          const url = getPdfUrl(
            `${stratusURL}bank/${applicationData.editslug}/${encodeURIComponent(item.value)}`,
            applicationData.progression_id >= 8 ? undefined : signerType
          );

          const is_pdf_exist = await isSignedPdfExist(url);
          if (is_pdf_exist && isMounted) {
            signedFiles.push(item);
          }
        });

        await Promise.all(checkPromises);

        if (isMounted) {
          setSignedFiles(signedFiles);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error checking signed files:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkFiles();

    return () => {
      isMounted = false;
    };
  }, [applicationData, stratusURL, signerType]);


  const handleFileTypeSubmit = async (fileTypes) => {
    try {
      const formData = new FormData();
      filesToUpload.forEach(file => {
        formData.append('files[]', file);
      });

      formData.append('fileTypes', JSON.stringify(fileTypes));
      formData.append('editslug', applicationData.editslug);
      const response = await uploadApplicationFiles(formData);
      if (response.status) {
        setShowTypeModal(false);
        setFilesToUpload([]);
        reRender();
        toast.success(`File uploaded successfully`);
      } else {
        toast.error(`File upload failed`);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error(`File upload failed`);
    }
  };

  const checkDocumentRequirements = (fileTypes) => {
    const file = appUploadedFiles.find(file => file.title === fileTypes);
    if (file) {
      return true;
    }
    return false;
  };

  return (
    <div className={styles.thankYouContainer}>
      <div className={styles.thankYouContent}>
        <div className={styles.mainWrapper}>
          <div className={styles.copyLinkButton} onClick={copyLinkToClipboard}>
            <Copy />
            <span className={styles.copyLinkText}>Copy link</span>
          </div>

          <div className={styles.content}>
            <div className={styles.headerWrapper}>
              <div className={styles.iconWrapper}>
                <div className={styles.iconCircle}>
                  <div className={styles.innerCircle}>
                    <CheckIcon />
                  </div>
                </div>
              </div>

              <div className={styles.headerText}>
                <h1 className={styles.title}>Thank You For Your Submission!</h1>
                <p className={styles.subtitle}>
                  Your application(s) and any supporting documents provided will be submitted to underwriting for expedited review. Please save the link to this page to track updates on your application process and to communicate with a live agent.
                </p>
              </div>
            </div>

            <ApplicationStepper currentStep={currentStep} />

            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Merchant account may require additional documentation such as:</h2>

              <div className={styles.checklistContainer}>
                {documentRequirements.map((doc, index) => (
                  <div key={index} className={styles.checklistItem}>
                    {checkDocumentRequirements(doc.type) ? <CheckCircleIcon /> : <CircleIcon />}
                    <span className={styles.itemLabel}>{doc.name}</span>
                  </div>
                ))}
              </div>

              <div
                className={`${styles.dragDrop} ${isDragOver ? styles.onDragOver : ''}`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById('fileUpload').click()}
                onMouseEnter={() => {
                  setIsDragOver(true);
                }}
                onMouseLeave={() => {
                  setIsDragOver(false);
                }}
                onDragEnter={() => {
                  setIsDragOver(true);
                }}
                onDragLeave={() => {
                  setIsDragOver(false);
                }}
              >
                <input
                  type="file"
                  id="fileUpload"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <div className={styles.dragDropContent}
                  onClick={() => document.getElementById('fileUpload').click()}
                  onMouseEnter={() => {
                    setIsDragOver(true);
                  }}
                  onMouseLeave={() => {
                    setIsDragOver(false);
                  }}
                  onDragEnter={() => {
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => {
                    setIsDragOver(false);
                  }}
                >
                  <div className={styles.dragDropText}>
                    Upload files by drag and drop or click to upload
                  </div>
                  <div className={styles.dragDropSubtext}>
                    jpg, jpeg, png, doc, docx, xlsx, xls, ppt, pptx, pages, keynote, numbers, pdf
                  </div>
                  <div className={styles.encryption}>
                    <LockIcon />
                    <span className={styles.encryptionText}>256-bit encryption</span>
                  </div>
                </div>
              </div>

              {appUploadedFiles.length > 0 && (
                <div className={styles.filesList}>
                  {appUploadedFiles.map((file, index) => (
                    <div
                      key={index}
                      className={styles.fileItem}
                      title={file.file}
                    >
                      <div className={styles.fileFormat}>{renderFileIcon(file.file)}</div>
                      <div className={styles.fileText}>
                        <div className={styles.fileName}>
                          {file.file.length > 20
                            ? `${file.file.substring(0, 10)}...${file.file.substring(file.file.length - 10)}`
                            : file.file}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.agreementsList}>
              <h3 className={styles.agreementsTitle}>The following agreements were also sent you via email.</h3>

              {isLoading ? (
                <div className={`${baseStyles.flex} ${baseStyles.justifyCenter} ${baseStyles.itemsCenter}`}>
                  <Spinner size={24} />
                </div>
              ) : (
                <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`}>
                  {signedFiles.map((agreement, index) => (
                    <div key={index} className={styles.fileItem}>
                      <div className={styles.fileFormat}>{renderFileIcon()}</div>
                      <div className={styles.fileText}>
                        <div className={styles.fileName}>{agreement.value}</div>
                        {/* <div className={styles.fileSize}>{agreement.size}</div> */}
                      </div>
                      <a
                        href={getPdfUrl(`${stratusURL}bank/${applicationData?.editslug}/${agreement.value}`,
                          applicationData.progression_id >= 8 ? undefined : signerType
                        )}
                        target="_blank"
                        rel="noreferrer"
                        className={`${baseStyles.flex} ${baseStyles.alignCenter}`}
                      >
                        <DownloadSimple />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.notesSidebar}>
        <NotesSection appData={appData} />
      </div>

      {showTypeModal && (
        <FileTypeModal
          isOpen={showTypeModal}
          onClose={() => {
            setShowTypeModal(false);
            setFilesToUpload([]);
            document.getElementById('fileUpload').value = '';
          }}
          files={filesToUpload}
          onSubmit={handleFileTypeSubmit}
        />
      )}

    </div>
  );
};

ThankYou.propTypes = {
  appData: PropTypes.object,
  appSlug: PropTypes.string
};

export default ThankYou; 